import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { vaultStatusApi, getVaultApi, createVaultApi, updateVaultApi } from 'core/features/sicherheit/sicherheitApi';
import { erstelleTresor, entsperreTresor, entsperreMitCode, verpackeNeu } from 'core/features/sicherheit/crypto';
import { setzeMasterKey, loescheMasterKey, istEntsperrt } from 'core/features/sicherheit/vaultHolder';
import { logout } from 'core/features/auth/authSlice';

/**
 * Redux-Slice fuer den "gesicherten Modus" (Tresor). Haelt nur den Tresor-Status;
 * der Master-Key selbst liegt bewusst ausserhalb des Stores im vaultHolder
 * (nicht serialisierbar, nur im Speicher). Gesicherte Module (Wissen,
 * Investitionen, ...) pruefen diesen Status und nutzen den geteilten Master-Key.
 */

/** Ermittelt, ob ein Tresor existiert bzw. bereits entsperrt ist. */
export const pruefeTresorStatus = createAsyncThunk('sicherheit/status', async () => {
  if (istEntsperrt()) return 'unlocked';
  const { vorhanden } = await vaultStatusApi();
  return vorhanden ? 'locked' : 'absent';
});

/** Richtet den Tresor erstmalig ein und entsperrt ihn sofort. */
export const richteTresorEin = createAsyncThunk('sicherheit/einrichten', async ({ passphrase, mitKeyfile }) => {
  const bundle = await erstelleTresor(passphrase, mitKeyfile);
  await createVaultApi(bundle.vault);
  setzeMasterKey(bundle.masterKey);
  return { recoveryCode: bundle.recoveryCode, keyfileBase64: bundle.keyfileBase64 };
});

/** Entsperrt den Tresor mit Passphrase (+ optionaler Schluesseldatei). */
export const entsperre = createAsyncThunk('sicherheit/entsperren', async ({ passphrase, keyfileBytes }) => {
  const vault = await getVaultApi();
  setzeMasterKey(await entsperreTresor(vault, passphrase, keyfileBytes));
});

/** Entsperrt ueber den Wiederherstellungs-Code und setzt eine neue Passphrase. */
export const entsperreMitRecovery = createAsyncThunk(
  'sicherheit/wiederherstellen',
  async ({ recoveryCode, neuePassphrase, mitKeyfile }) => {
    const vault = await getVaultApi();
    const { masterKey, masterBytes } = await entsperreMitCode(vault, recoveryCode);
    const neu = await verpackeNeu(masterBytes, neuePassphrase, mitKeyfile, vault);
    await updateVaultApi(neu.vault);
    setzeMasterKey(masterKey);
    return { keyfileBase64: neu.keyfileBase64 };
  }
);

const initialState = {
  status: 'unknown', // unknown | absent | locked | unlocked
};

const sicherheitSlice = createSlice({
  name: 'sicherheit',
  initialState,
  reducers: {
    /** Verlaesst den gesicherten Modus: Master-Key vergessen. */
    sperren(state) {
      loescheMasterKey();
      state.status = 'locked';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(pruefeTresorStatus.fulfilled, (state, action) => {
        state.status = action.payload;
      })
      .addCase(richteTresorEin.fulfilled, (state) => {
        state.status = 'unlocked';
      })
      .addCase(entsperre.fulfilled, (state) => {
        state.status = 'unlocked';
      })
      .addCase(entsperreMitRecovery.fulfilled, (state) => {
        state.status = 'unlocked';
      })
      // Abmelden verlaesst auch den gesicherten Modus.
      .addCase(logout, (state) => {
        loescheMasterKey();
        state.status = 'unknown';
      });
  },
});

export const { sperren } = sicherheitSlice.actions;

// ── Selektoren ──────────────────────────────────────────
export const selectTresorStatus = (state) => state.sicherheit.status;
export const selectIstGesichert = (state) => state.sicherheit.status === 'unlocked';

export default sicherheitSlice.reducer;
